import React, { createContext, useContext, useState, useEffect } from 'react';
import { translations } from './translations';

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    // Check localStorage first
    const saved = localStorage.getItem('nexair-language');
    if (saved && (saved === 'es' || saved === 'en')) {
      return saved;
    }
    
    // Auto-detect from browser
    const browserLang = navigator.language || navigator.userLanguage;
    // If browser language starts with 'es', use Spanish, otherwise English
    return browserLang.startsWith('es') ? 'es' : 'en';
  });

  // Save to localStorage whenever language changes
  useEffect(() => {
    localStorage.setItem('nexair-language', language);
  }, [language]);

  const t = (key) => {
    const keys = key.split('.');
    let value = translations[language];
    
    for (const k of keys) {
      if (value && typeof value === 'object') {
        value = value[k];
      } else {
        return key; // Return key if translation not found
      }
    }
    
    return value || key;
  };

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'es' ? 'en' : 'es');
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// Language Selector Component with Flags
export const LanguageSelector = ({ className = '' }) => {
  const { language, setLanguage } = useLanguage();

  return (
    <div className={`flex items-center gap-1 ${className}`}>
      <button
        onClick={() => setLanguage('es')}
        className={`flex items-center justify-center w-8 h-8 rounded-full transition-all ${
          language === 'es' 
            ? 'ring-2 ring-cyan-400 ring-offset-2 ring-offset-transparent' 
            : 'opacity-50 hover:opacity-100'
        }`}
        title="Español"
        aria-label="Cambiar a Español"
        data-testid="lang-es"
      >
        <span className="text-xl" role="img" aria-label="Mexico flag">🇲🇽</span>
      </button>
      <button
        onClick={() => setLanguage('en')}
        className={`flex items-center justify-center w-8 h-8 rounded-full transition-all ${
          language === 'en' 
            ? 'ring-2 ring-cyan-400 ring-offset-2 ring-offset-transparent' 
            : 'opacity-50 hover:opacity-100'
        }`}
        title="English"
        aria-label="Switch to English"
        data-testid="lang-en"
      >
        <span className="text-xl" role="img" aria-label="USA flag">🇺🇸</span>
      </button>
    </div>
  );
};

export default LanguageContext;
